import React, {useState} from 'react';
import ExtraFieldForm from './ExtraFieldForm';
import Drawer from '@/components/Drawer';
import { useSubmitOnEnter } from '@/components/Form/useSubmitOnEnter';
import {Button, FormInstance, Modal, Space} from 'antd';
import {useDispatch, useIntl} from 'umi';
import {DeleteOutlined, ExclamationCircleOutlined} from '@ant-design/icons';
import {ExtraFieldTypeModel} from '@/typings/models/ExtraFields';
import {
  createExtraFieldTypeRequest,
  deleteExtraFieldTypeRequest,
  editExtraFieldTypeRequest,
  getExtraFieldTypeValueCount,
} from '@/services/api/extraFieldType';
import {CheckAccess} from '@/utils/access';
import PERMISSIONS from '@/constants/permissions';
import {processAPIResponseNow} from '@/utils/request';
import {F, INTL_ACTION, INTL_ENTITY} from '@/utils/intl';
import isEqual from 'lodash/isEqual';

interface Props {
  editingField: ExtraFieldTypeModel | null;
  onClose: () => void;
  setEditingField: (field: ExtraFieldTypeModel) => void;
  isNew: boolean;
  onFieldCreated?: (field: ExtraFieldTypeModel) => void;
}

const FETCH_EXTRA_FIELDS_ACTION = 'extraFields/fetchExtraFieldTypes';

const ExtraFieldDrawer: React.FC<Props> = ({ editingField, onClose, setEditingField, isNew, onFieldCreated }) => {
  const [formInstance, setFormInstance] = useState<FormInstance>(null);
  const [editLoading, setEditLoading] = useState(false);
  const dispatch = useDispatch();
  const intl = useIntl();

  const handleClose = () => {
    setFormInstance(null);
    reload();
    onClose();
  };


  const reload = () => dispatch({ type: FETCH_EXTRA_FIELDS_ACTION });
  const reloadAndClose = () => {
    reload();
    handleClose();
  };

  const handleCreate = (values: Record<string, unknown>) => {
    setEditLoading(true);
    createExtraFieldTypeRequest({ data: { ...values, is_editable: true } })
      .then((response) => {
        setEditLoading(false);
        processAPIResponseNow(response, {
          onSuccess(data) {
            if (onFieldCreated && data?.data) onFieldCreated(data.data);
            reloadAndClose();
          },
        });
      })
      .catch(() => setEditLoading(false));
  };

  const handleEdit = (values: Record<string, unknown>) => {
    const hasChanges = Object.keys(values).some(key => !isEqual(values[key], editingField[key]));
    if (!hasChanges) return;

    setEditLoading(true);
    editExtraFieldTypeRequest({
      params: { extraFieldTypeId: editingField.uuid },
      data: { ...editingField, ...values },
    })
      .then((response) => {
        setEditLoading(false);
        processAPIResponseNow(response, {
          showSuccessMessage: true,
          successEntity: INTL_ENTITY.EXTRA_FIELD,
        });
      })
      .catch(() => setEditLoading(false));
  };

  const handleSubmit = () => {
    formInstance?.validateFields().then((values) => {
      isNew ? handleCreate(values) : handleEdit(values);
    });
  };

  const handleDeleteClick = async () => {
    const countResponse = await getExtraFieldTypeValueCount({
      params: { extraFieldTypeId: editingField.uuid },
    }).catch(() => null);

    const valueCount = countResponse?.data?.data?.total_count ?? 0;

    Modal.confirm({
      title: intl.formatMessage(
        { id: 'common.entity.remove' },
        { entity: intl.formatMessage({ id: 'entity.extra_field.singular' }) },
      ),
      icon: <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />,
      content: getDeleteModalContent(valueCount),
      okText: intl.formatMessage({ id: 'common.actions.delete_permanently' }),
      okType: 'danger',
      cancelText: intl.formatMessage({ id: 'common.actions.cancel' }),
      onOk: () => new Promise((resolve, reject) => {
        deleteExtraFieldTypeRequest({ params: { extraFieldTypeId: editingField.uuid } })
          .then((response) => processAPIResponseNow(response, {
            onSuccess: reloadAndClose,
            onEnd: () => resolve(true),
          }))
          .catch(reject);
      }),
    });
  };

  const getDeleteModalContent = (valueCount: number) => {
    if (valueCount === 0) {
      return <p>{intl.formatMessage({ id: 'pages.extraFieldType.delete.confirmNoValues' }, { label: editingField.label })}</p>;
    }
    return (
      <div>
        <p style={{ marginBottom: 16 }}>
          {intl.formatMessage({ id: 'pages.extraFieldType.delete.confirmWithValues' }, { label: editingField.label, count: valueCount })}
        </p>
        <p style={{ color: '#ff4d4f', fontWeight: 500 }}>
          {intl.formatMessage({ id: 'common.actions.cannot_be_undone' })}
        </p>
      </div>
    );
  };

  const submitOnEnter = useSubmitOnEnter(handleSubmit, {
    enabled: !editLoading,
  });

  const footer = (
    <div style={{ textAlign: 'right' }}>
      <Space>
        <CheckAccess access={[PERMISSIONS.ExtraFieldType.DELETE, PERMISSIONS.BUSINESS_PARTNER]}>
          {!isNew && (
            <Button size="large" danger icon={<DeleteOutlined />} onClick={handleDeleteClick}>
              <F id="common.actions.remove" defaultMessage="Remove" />
            </Button>
          )}
        </CheckAccess>
        <Button className="button-secondary button-bordered" size="large" type="primary" onClick={handleClose}>
          <F id="common.actions.close" defaultMessage="Close" />
        </Button>
        {isNew && (
          <Button loading={editLoading} size="large" type="primary" onClick={handleSubmit}>
            <F id="common.actions.create" defaultMessage="Create" />
          </Button>
        )}
      </Space>
    </div>
  );

  return (
    <Drawer
      title={<F entity={INTL_ENTITY.EXTRA_FIELD} action={isNew ? INTL_ACTION.CREATE : INTL_ACTION.EDIT} />}
      open={!!editingField}
      onOpenChange={(open) => !open && reloadAndClose()}
      footer={footer}
      destroyOnHidden
    >
      <div {...submitOnEnter}>
        <ExtraFieldForm
          onFormReady={setFormInstance}
          onEdit={handleSubmit}
          editingField={editingField}
          setEditingField={setEditingField}
          isNew={isNew}
        />
      </div>
    </Drawer>
  );
};

export default ExtraFieldDrawer;
